import { useRouter } from '@cp/shared/router';
import { PropsWithChildren, useCallback, useEffect } from 'react';
import { URN } from '@youhelp-ru/urn';
import { useUpdateQueryParams } from '@cp/shared/queryParams';
import { useEventBus, useEventBusSubscribe } from 'src/shared/eventBus';
import { useModal } from 'src/shared/modalProvider';
import { useStartBillingSubscription } from 'src/features/billing';

type ActionEvent = { name: 'action'; action: string; params: Iterable<[string, string]> };

export const ModalRunner = (props: PropsWithChildren) => {
  const subscribe = useEventBusSubscribe<ActionEvent>();
  const { openModal, closeModal } = useModal();
  const updateQueryParams = useUpdateQueryParams();

  const dropActionParam = useCallback(
    (name: string) => {
      updateQueryParams({ action: undefined });
      closeModal(name);
    },
    [updateQueryParams, closeModal],
  );

  useEffect(() => {
    return subscribe({ name: 'action' }, (event) => {
      const { action, params } = event;
      if (!URN.canParse(action)) {
        return;
      }
      const urn = new URN(action);
      if (urn.nid !== 'modal') {
        return;
      }
      const modalName = urn.nss;
      const mp = Object.fromEntries(params);
      const onClose = dropActionParam.bind(null, modalName);
      // @ts-expect-error modalProps should be type coerced
      openModal(modalName, { ...mp, onModalDestroy: onClose, onCancel: onClose, onSuccess: onClose });
    });
  }, [subscribe, dropActionParam, openModal]);
  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{props.children}</>;
};

const FeatureRunner = (props: PropsWithChildren) => {
  const subscribe = useEventBusSubscribe<ActionEvent>();
  const [startSubsctiption] = useStartBillingSubscription();
  useEffect(() => {
    return subscribe({ name: 'action' }, (event) => {
      const { action } = event;
      if (!URN.canParse(action)) {
        return;
      }
      const { nid, nss: featureName } = new URN(action);
      if (nid !== 'feature') {
        return;
      }
      console.debug(`Run ${featureName}`);
      // NOTE: experimental feature do not rely on this. Used only in useRegistrationService
      if (featureName === 'start-subscription') {
        startSubsctiption();
      }
    });
  }, [subscribe, startSubsctiption]);
  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{props.children}</>;
};

export const ActionRunner = (props: PropsWithChildren) => {
  const router = useRouter();
  const eventBus = useEventBus();
  useEffect(() => {
    const url = new URL(router.location.href);
    if (url.searchParams.has('action')) {
      eventBus.publish({ name: 'action', action: url.searchParams.get('action'), params: url.searchParams.entries() });
    }
  }, [router.location, eventBus]);

  return (
    <ModalRunner>
      <FeatureRunner>{props.children}</FeatureRunner>
    </ModalRunner>
  );
};
